import type { ComponentType } from 'react'
import type { RouteId } from '@core/routing/router.config'
import { LoginRegisterPageComponent } from '@core/infra/authentication/login-register-page.component'
import { GamesPageComponent } from '@features/games/games-page.component'
import { NotFoundPageComponent } from '@core/infra/not-found/not-found-page.component'

/**
 * A route's page is either mounted eagerly (`Login`, `Home`, `NotFound`) or
 * loaded on demand. The loader form is a plain `{ load }`, not a
 * `React.lazy` component: `React.lazy` caches a rejected import forever, so
 * retrying a failed chunk fetch needs a fresh loader call each time —
 * `PageSlotComponent` (`layout/page-boundary.component.tsx`) is what turns
 * this into a `React.lazy` component per attempt.
 */
export type PageEntry<P extends object = object> = ComponentType<P> | { readonly load: () => Promise<ComponentType<P>> }

/**
 * Wraps a named-export page module as a `PageEntry` loader — `React.lazy`
 * only accepts a default export, and every page component here is a named
 * one, so `PageSlotComponent` does that wrapping itself once it actually
 * attempts the load.
 */
function pageLoader<P extends object>(
  load: () => Promise<Record<string, ComponentType<P>>>,
  exportName: string,
): PageEntry<P> {
  return { load: () => load().then((module) => module[exportName]) }
}

/**
 * RouteId → page entry. Complete by construction: adding a route id without
 * registering an entry here is a compile error.
 *
 * `Login`, `Home` and `NotFound` stay eager — reachable on first paint before
 * any route-specific chunk could load. Every other page is a separate chunk:
 * this was previously one 1.25 MB bundle shipped to every visitor, most of it
 * admin pages a non-admin user never opens.
 */
export const routeComponents: Record<RouteId, PageEntry> = {
  Login: LoginRegisterPageComponent,
  NotFound: NotFoundPageComponent,
  Home: GamesPageComponent,
  Lobby: pageLoader(() => import('@features/lobby/lobby-page.component'), 'LobbyPageComponent'),
  'Game.Setup': pageLoader(() => import('@features/game/game-setup-page.component'), 'GameSetupPageComponent'),
  'Game.Play': pageLoader(() => import('@features/game/game-play-page.component'), 'GamePlayPageComponent'),

  Books: pageLoader(() => import('@features/books/books-page.component'), 'BooksPageComponent'),
  'HR.Users': pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  'HR.User': pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  'Admin.Errors': pageLoader(
    () => import('@core/infra/observability/errors/admin-error-lookup-page.component'),
    'AdminErrorLookupPageComponent',
  ),
  'Admin.Users': pageLoader(
    () => import('@core/infra/authentication/admin/admin-users-page.component'),
    'AdminUsersPageComponent',
  ),
  'Admin.Audit': pageLoader(
    () => import('@core/infra/observability/audit/admin-audit-page.component'),
    'AdminAuditPageComponent',
  ),
  InvisiblePage: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  UnClickablePage: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ClickablePage: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ExpandablePage: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ExpandablePageChild1: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ExpandablePageChild2: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  BothPage: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  BothPageChild1: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  BothPageChild2: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  BothPageBoth: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  BothPageBothChild1: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  BothPageBothChild2: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ColumnsPage: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ColumnsPageSettings: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ColumnsPageAdvanced: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ColumnsPageAdvancedOptionA: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  ColumnsPageAdvancedOptionB: pageLoader(() => import('./pages/route-info.component'), 'RouteInfoComponent'),
  InfraDemoPage: pageLoader(() => import('@features/infra-demo/infra-demo-page.component'), 'InfraDemoPageComponent'),
}
